import { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { OfflineProvider, useOffline } from './contexts/OfflineContext';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import GuidedTour from './components/GuidedTour';
import SilentAuth from './components/SilentAuth';

// Import pages
import UploadPage from './pages/UploadPage';
import SearchChunksPage from './pages/SearchChunksPage';
import SelectedChunksPage from './pages/SelectedChunksPage';
import ExportPage from './pages/ExportPage';
import UserProfilePage from './pages/UserProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccess from './pages/PaymentSuccess';
import NotFoundPage from './pages/NotFoundPage';
import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

function SessionDropdown() {
  const { sessions, currentSessionId, switchSession, addSession, deleteSession } = useSession();
  const { isOnline } = useOffline();
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const newSessionCreateButtonRef = useRef<HTMLButtonElement>(null);
  const newSessionCancelButtonRef = useRef<HTMLButtonElement>(null);
  const deleteSessionButtonRef = useRef<HTMLButtonElement>(null);
  const deleteSessionCancelButtonRef = useRef<HTMLButtonElement>(null);

  const handleCreateSession = () => {
    if (newSessionName.trim()) {
      addSession(newSessionName.trim()); // Session switching is now handled internally
      // No need to clear search results - new session is already created with empty results
      setNewSessionName('');
      setShowNewSessionModal(false);
    }
  };

  const handleDeleteSession = () => {
    if (Object.keys(sessions).length > 1) {
      const success = deleteSession(currentSessionId);
      if (success) {
        // Search results will be restored from the new session automatically
        setShowDeleteConfirm(false);
      }
    }
  };

  const handleSwitchSession = (sessionId: string) => {
    switchSession(sessionId);
    // Don't clear search results - let them restore from the session
  };

  // Keyboard navigation for new session modal
  useEffect(() => {
    if (!showNewSessionModal) return;

    const handleModalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        // Toggle focus between create and cancel buttons
        if (document.activeElement === newSessionCreateButtonRef.current) {
          newSessionCancelButtonRef.current?.focus();
        } else {
          newSessionCreateButtonRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleModalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleModalKeyDown);
    };
  }, [showNewSessionModal]);

  // Keyboard navigation for delete session modal
  useEffect(() => {
    if (!showDeleteConfirm) return;

    const handleModalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        // Toggle focus between delete and cancel buttons
        if (document.activeElement === deleteSessionButtonRef.current) {
          deleteSessionCancelButtonRef.current?.focus();
        } else {
          deleteSessionButtonRef.current?.focus();
        }
      }
    };

    window.addEventListener('keydown', handleModalKeyDown);

    return () => {
      window.removeEventListener('keydown', handleModalKeyDown);
    };
  }, [showDeleteConfirm]);

  const canDeleteCurrentSession = Object.keys(sessions).length > 1;

  return (
    <div className="session-controls">
      <select
        value={currentSessionId}
        onChange={(e) => handleSwitchSession(e.target.value)}
        className="session-dropdown"
        aria-label="session"
        disabled={!isOnline}
        title={!isOnline ? "Session switching requires internet connection" : ""}
      >
        {Object.entries(sessions).map(([id, session]) => (
          <option key={id} value={id}>
            {session.name}
          </option>
        ))}
      </select>
      <button
        onClick={() => setShowNewSessionModal(true)}
        className="new-session-btn"
        title={!isOnline ? "Create session requires internet connection" : "Create new session"}
        disabled={!isOnline}
      >
        +
      </button>
      {canDeleteCurrentSession && (
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="delete-session-btn"
          title={!isOnline ? "Delete session requires internet connection" : "Delete current session"}
          disabled={!isOnline}
        >
          ×
        </button>
      )}

      {showNewSessionModal && (
        <div className="modal-overlay" onClick={() => setShowNewSessionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Session</h3>
            <input
              type="text"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              placeholder="Session name"
              className="session-name-input"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateSession()}
              autoFocus
            />
            <div className="modal-buttons">
              <button
                ref={newSessionCreateButtonRef}
                onClick={handleCreateSession}
                disabled={!newSessionName.trim()}
              >
                Create
              </button>
              <button
                ref={newSessionCancelButtonRef}
                onClick={() => setShowNewSessionModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Session</h3>
            <p>Are you sure you want to delete "{sessions[currentSessionId]?.name}"?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button
                ref={deleteSessionButtonRef}
                onClick={handleDeleteSession}
                className="delete-btn"
                autoFocus
              >
                Delete
              </button>
              <button
                ref={deleteSessionCancelButtonRef}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Navigation() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isOnline } = useOffline();
  const { getSelectedChunks, currentSessionId, registerSelectedCountRefreshCallback, unregisterSelectedCountRefreshCallback } = useSession();
  const [selectedCount, setSelectedCount] = useState(0);
  const [isSettingsRotating, setIsSettingsRotating] = useState(false);
  const rotationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getSelectedChunksRef = useRef(getSelectedChunks);
  const navRef = useRef<HTMLElement>(null);

  // Check if there's a stored token for upload access when offline
  const hasStoredToken = !!localStorage.getItem('google_auth_token');
  const canAccessUpload = isAuthenticated || (!isOnline && hasStoredToken);

  // Update main element padding/height directly based on nav height
  useEffect(() => {
    const updateMainPadding = () => {
      if (navRef.current) {
        const navHeight = navRef.current.offsetHeight;

        // Find the main element
        const mainElement = document.querySelector('.App-main') as HTMLElement;
        if (mainElement) {
          // Temporarily disable transitions to prevent visual shift
          mainElement.style.transition = 'none';

          // For mobile (fixed nav), set padding-top
          if (window.innerWidth <= 768) {
            mainElement.style.paddingTop = navHeight + 'px';
          } else {
            // For desktop, set height calculation
            mainElement.style.height = `calc(100vh - ${navHeight}px)`;
            mainElement.style.maxHeight = `calc(100vh - ${navHeight}px)`;
          }

          // Force a reflow to apply the styles immediately
          void mainElement.offsetHeight;

          // Re-enable transitions after a frame
          requestAnimationFrame(() => {
            if (mainElement) {
              mainElement.style.transition = '';
            }
          });
        }
      }
    };

    // Update immediately - no delay
    updateMainPadding();
    window.addEventListener('resize', updateMainPadding);

    return () => {
      window.removeEventListener('resize', updateMainPadding);
    };
  }, []);

  // Update when nav content changes - also immediate
  useEffect(() => {
    const updateMainPadding = () => {
      if (navRef.current) {
        const navHeight = navRef.current.offsetHeight;

        const mainElement = document.querySelector('.App-main') as HTMLElement;
        if (mainElement) {
          // Disable transitions for smooth update
          mainElement.style.transition = 'none';

          if (window.innerWidth <= 768) {
            mainElement.style.paddingTop = navHeight + 'px';
          } else {
            mainElement.style.height = `calc(100vh - ${navHeight}px)`;
            mainElement.style.maxHeight = `calc(100vh - ${navHeight}px)`;
          }

          // Force reflow
          void mainElement.offsetHeight;

          // Re-enable transitions
          requestAnimationFrame(() => {
            if (mainElement) {
              mainElement.style.transition = '';
            }
          });
        }
      }
    };

    // Use requestAnimationFrame instead of setTimeout for smoother timing
    requestAnimationFrame(updateMainPadding);
  }, [isAuthenticated, selectedCount, canAccessUpload, location.pathname]);

  // Keep ref up to date
  useEffect(() => {
    getSelectedChunksRef.current = getSelectedChunks;
  }, [getSelectedChunks]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (rotationTimeoutRef.current) {
        clearTimeout(rotationTimeoutRef.current);
      }
    };
  }, []);

  // Fetch selected count when session changes or component mounts
  useEffect(() => {
    // Clear count immediately when not authenticated
    if (!isAuthenticated) {
      setSelectedCount(0);
      return;
    }

    const fetchSelectedCount = async () => {
      try {
        const chunks = await getSelectedChunksRef.current();
        setSelectedCount(chunks.length);
      } catch (error) {
        console.error('Error fetching selected count:', error);
        setSelectedCount(0);
      }
    };

    fetchSelectedCount();
    // Refetch when currentSessionId changes or auth state changes
  }, [currentSessionId, isAuthenticated]);

  // Register callback to refresh selected count when needed
  useEffect(() => {
    const handleSelectedCountChange = async (action: 'increment' | 'decrement' | 'refresh') => {
      if (action === 'increment') {
        // Optimistic update: increment immediately
        setSelectedCount(prev => prev + 1);
      } else if (action === 'decrement') {
        // Optimistic update: decrement immediately
        setSelectedCount(prev => Math.max(0, prev - 1));
      } else if (action === 'refresh') {
        // Fetch actual count from backend using ref to get latest function
        try {
          const chunks = await getSelectedChunksRef.current();
          setSelectedCount(chunks.length);
        } catch (error) {
          console.error('Error refreshing selected count:', error);
        }
      }
    };

    registerSelectedCountRefreshCallback(handleSelectedCountChange);

    return () => {
      unregisterSelectedCountRefreshCallback();
    };
    // Remove getSelectedChunks from deps since we use the ref
  }, [registerSelectedCountRefreshCallback, unregisterSelectedCountRefreshCallback]);

  const isActive = (path: string) => {
    if (path === '/search') {
      return location.pathname === '/' || location.pathname === '/search';
    }
    return location.pathname === path;
  };

  const isUploadPage = location.pathname === '/upload';
  const isSettingsPage = location.pathname === '/settings';

  const handleSettingsClick = useCallback(() => {
    setIsSettingsRotating(true);
    rotationTimeoutRef.current = setTimeout(() => setIsSettingsRotating(false), 500);
  }, []);

  return (
    <nav ref={navRef} className="App-nav">
      <div className="nav-links">
        <Link to="/upload" className={`nav-link ${!canAccessUpload ? 'disabled' : ''} ${isActive('/upload') ? 'active' : ''}`}>Upload</Link>
        <Link to="/search" className={`nav-link ${!isAuthenticated || !isOnline ? 'disabled' : ''} ${isActive('/search') ? 'active' : ''}`}>Search</Link>
        <Link to="/selected" className={`nav-link ${!isAuthenticated || !isOnline ? 'disabled' : ''} ${isActive('/selected') ? 'active' : ''}`}>
          Selected {selectedCount > 0 && <span className="count">({selectedCount})</span>}
        </Link>
        <Link to="/export" className={`nav-link ${!isAuthenticated || !isOnline ? 'disabled' : ''} ${isActive('/export') ? 'active' : ''}`}>Export</Link>
        <Link
          to="/settings"
          className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
          title="Settings"
          onClick={handleSettingsClick}
        >
          <span className={isSettingsRotating ? 'rotating' : ''}>⚙️</span>
        </Link>
      </div>
      {!isUploadPage && !isSettingsPage && isAuthenticated && <SessionDropdown />}
    </nav>
  );
}

export function AppContent() {
  const { isAuthenticated, sessionExpired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Navigate to settings page when session expires or when not authenticated
  useEffect(() => {
    // If session expired, always redirect to settings
    if (sessionExpired && location.pathname !== '/settings') {
      navigate('/settings', { replace: true });
    }
    // If not authenticated and not on a public page, redirect to settings
    else if (!isAuthenticated && location.pathname !== '/settings' &&
             location.pathname !== '/home' && location.pathname !== '/privacy' &&
             location.pathname !== '/') {
      navigate('/settings', { replace: true });
    }
  }, [sessionExpired, isAuthenticated, location.pathname, navigate]);

  // Pages that don't show navigation (public landing pages)
  const isPublicPage = location.pathname === '/home' || location.pathname === '/privacy';
  // Show landing page at root for non-authenticated users
  const showLandingAtRoot = location.pathname === '/' && !isAuthenticated;

  return (
    <div className="App">
      {!isPublicPage && !showLandingAtRoot && <Navigation />}

      {/* Silent authentication component for seamless re-authentication */}
      <SilentAuth />

      <main className={`App-main ${isPublicPage || showLandingAtRoot ? 'full-height' : ''}`}>
        <Routes>
          <Route path="/" element={isAuthenticated ? <ProtectedRoute><SearchChunksPage /></ProtectedRoute> : <LandingPage />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchChunksPage /></ProtectedRoute>} />
          <Route path="/selected" element={<ProtectedRoute><SelectedChunksPage /></ProtectedRoute>} />
          <Route path="/export" element={<ProtectedRoute><ExportPage /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/settings" element={<UserProfilePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <GuidedTour />
    </div>
  );
}

function App() {
  return (
    <SessionProvider>
      <OfflineProvider>
        <Router>
          <AppContent />
        </Router>
      </OfflineProvider>
    </SessionProvider>
  );
}

export default App;
